/**
 * Summary sheet: row counts and breakdown by data-contract check type.
 */

import { motion } from "framer-motion";
import { BarChart3 } from "lucide-react";
import { Spinner, Button } from "@/components/reusable";
import {
  DQX_SUMMARY_CHECK_TYPE_ORDER,
  type DqxRowLevelSummary,
  type DqxSummaryCheckTypeBucket,
} from "@/services/dqxSummaryMetrics";

export type ContractRulesSummaryStatus = "idle" | "loading" | "success" | "error";

const BUCKET_DEFINITIONS: Record<DqxSummaryCheckTypeBucket, string> = {
  "data type":
    "Checks that field values match the declared format. For example, date fields such as Loss Date and Instruction Date must contain a valid date value.",
  mandatory:
    "Checks that required fields are present and not empty. Fields such as Supplier Name, Claim Number and Policy Number must be populated in every row.",
  summation:
    "Checks that numeric totals reconcile with their component fields. For example, Total Indemnity Spend must equal the sum of Building, Contents, Repair, Replacement and Cash Settlement Indemnity Spend.",
  conditional:
    "Checks that values satisfy cross-field logic and ordering rules, including numeric min/max ranges. For example, Loss Date must fall on or before Instruction Date, no dates may be set in the future, and numeric values must stay within defined bounds.",
  duplicates:
    "Checks that combinations of fields are unique across all rows. For example, the combination of Policy Number and Claim Number must not appear more than once.",
  pattern:
    "Checks that values match a required format. For example, Policy Number must follow the format 'ABC 123456789' and be fully uppercase.",
  "reference list":
    "Checks that values belong to an approved set. For example, Claim Originator must be one of: Policyholder, Broker, Third Party, Solicitor, Loss Assessor, or Other.",
  other: "Rows with failures that could not be matched to a specific check type.",
};

function formatBucketLabel(bucket: DqxSummaryCheckTypeBucket): string {
  if (bucket === "reference list") return "Reference List";
  if (bucket === "data type") return "Data Type";
  return bucket.charAt(0).toUpperCase() + bucket.slice(1);
}

function bucketIsClickable(
  bucket: DqxSummaryCheckTypeBucket,
  status: ContractRulesSummaryStatus
): boolean {
  if (bucket === "other") {
    return status === "loading" || status === "success" || status === "error";
  }
  return status === "success";
}

interface DqxSummaryPanelProps {
  summary: DqxRowLevelSummary;
  contractRulesStatus: ContractRulesSummaryStatus;
  /** Per-bucket counts shown on cards (may differ from summary while contract rules load). */
  breakdownDisplayCounts: Record<DqxSummaryCheckTypeBucket, number>;
  /** Per-bucket failing cell counts. */
  breakdownCellCounts: Record<DqxSummaryCheckTypeBucket, number>;
  selectedBuckets: DqxSummaryCheckTypeBucket[];
  onToggleBucket: (bucket: DqxSummaryCheckTypeBucket) => void;
  onClearSelections: () => void;
}

export function DqxSummaryPanel({
  summary,
  contractRulesStatus,
  breakdownDisplayCounts,
  breakdownCellCounts,
  selectedBuckets,
  onToggleBucket,
  onClearSelections,
}: DqxSummaryPanelProps) {
  const showBreakdownSpinner = contractRulesStatus === "loading";
  const breakdownUnavailable = contractRulesStatus === "error";
  const hasSelection = selectedBuckets.length > 0;

  const showBreakdownGrid =
    contractRulesStatus === "success" ||
    contractRulesStatus === "loading" ||
    contractRulesStatus === "error";

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.12 }}
      aria-labelledby="dqx-summary-heading"
      className="rounded-xl border border-[var(--color-border-light)] bg-[var(--color-bg-secondary)]/80 px-5 py-4 mb-6"
    >
      <div className="flex items-start gap-3 mb-4">
        <BarChart3
          className="w-5 h-5 text-[var(--color-brand-primary)] shrink-0 mt-0.5"
          aria-hidden
        />
        <div className="min-w-0">
          <h2
            id="dqx-summary-heading"
            className="text-sm font-semibold text-[var(--color-text-primary)]"
          >
            Summary
          </h2>
          <p className="text-xs text-[var(--color-text-tertiary)] mt-0.5 leading-relaxed">
            Row-level results for this run
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <div className="rounded-lg border border-[var(--color-border-light)] bg-white/60 px-4 py-3">
          <p className="text-xs font-medium text-[var(--color-text-tertiary)] uppercase tracking-wide">
            Total rows checked
          </p>
          <p className="text-2xl font-semibold tabular-nums text-[var(--color-text-primary)] mt-1">
            {summary.totalRowsChecked}
          </p>
        </div>
        <div className="rounded-lg border border-emerald-200/80 bg-emerald-50/50 px-4 py-3">
          <p className="text-xs font-medium text-emerald-800/80 uppercase tracking-wide">
            Passing all checks
          </p>
          <p className="text-2xl font-semibold tabular-nums text-emerald-900 mt-1">
            {summary.rowsPassingAllChecks}
          </p>
        </div>
        <div className="rounded-lg border border-red-200/80 bg-red-50/50 px-4 py-3">
          <p className="text-xs font-medium text-red-900/80 uppercase tracking-wide">
            At least one failure
          </p>
          <p className="text-2xl font-semibold tabular-nums text-red-950 mt-1">
            {summary.rowsWithAtLeastOneFailure}
          </p>
          <p className="text-[11px] text-red-900/70 mt-1 leading-snug">
            Includes rows with errors or warnings.
          </p>
        </div>
      </div>

      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <p className="text-xs font-semibold text-[var(--color-text-primary)]">
            Failing rows by check type
          </p>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onClearSelections}
            disabled={!hasSelection}
          >
            Clear selections
          </Button>
        </div>
        {showBreakdownSpinner && (
          <div
            className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)] py-2"
            role="status"
            aria-live="polite"
          >
            <Spinner size="sm" />
            <span>Loading contract rules for breakdown…</span>
          </div>
        )}
        {breakdownUnavailable && !showBreakdownSpinner && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-2">
            Breakdown typing unavailable: data contract rules could not be loaded. Headline counts
            above still reflect quarantine results.
          </p>
        )}
        {showBreakdownGrid && (
          <ul
            className={[
              "grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 list-none p-0 m-0 transition-opacity",
              showBreakdownSpinner ? "opacity-60 pointer-events-none" : "",
            ].join(" ")}
            aria-busy={showBreakdownSpinner}
          >
            {DQX_SUMMARY_CHECK_TYPE_ORDER.filter((k) => k !== "other").map((bucket) => {
              const clickable = bucketIsClickable(bucket, contractRulesStatus);
              const selected = selectedBuckets.includes(bucket);
              const count = breakdownDisplayCounts[bucket];
              const cellCount = breakdownCellCounts[bucket];
              return (
                <li key={bucket} className="relative group m-0">
                  <button
                    type="button"
                    disabled={!clickable || showBreakdownSpinner}
                    aria-pressed={selected}
                    onClick={() => onToggleBucket(bucket)}
                    className={[
                      "w-full rounded-md border px-2.5 py-2 text-center transition-colors",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand-primary)]/40 focus-visible:ring-offset-2",
                      selected
                        ? "border-[var(--color-brand-primary)] bg-[var(--color-brand-primary)]/10 shadow-sm"
                        : "border-[var(--color-border-light)] bg-white/50",
                      clickable && !showBreakdownSpinner
                        ? `cursor-pointer ${selected ? "" : "hover:bg-white/90 hover:border-[var(--color-border-light)]"}`
                        : "cursor-not-allowed opacity-50",
                    ].join(" ")}
                  >
                    <span className="block text-[10px] font-medium text-[var(--color-text-tertiary)] leading-tight">
                      {formatBucketLabel(bucket)}
                    </span>
                    <span className="block text-lg font-semibold tabular-nums text-[var(--color-text-primary)] mt-0.5">
                      {showBreakdownSpinner ? "—" : count}
                    </span>
                    {!showBreakdownSpinner && (
                      <span className="block text-[10px] tabular-nums text-[var(--color-text-tertiary)] leading-tight">
                        {cellCount} {cellCount === 1 ? "cell" : "cells"} impacted
                      </span>
                    )}
                  </button>
                  <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 rounded-md border border-[var(--color-border-light)] bg-white shadow-lg px-3 py-2 z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                    <p className="text-[11px] font-semibold text-[var(--color-text-primary)] mb-0.5">
                      {formatBucketLabel(bucket)}
                    </p>
                    <p className="text-[11px] text-[var(--color-text-secondary)] leading-snug">
                      {BUCKET_DEFINITIONS[bucket]}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </motion.section>
  );
}
